import startCase from 'lodash/startCase'
import { getToken } from './authToken'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/** A single FastAPI/pydantic 422 field error, as returned in a `detail` array. */
interface FastApiValidationError {
  loc: (string | number)[]
  msg: string
  ctx?: { reason?: string }
}

/** The two shapes the backend's error `detail` comes in: a plain string (400-style), or per-field validation errors (422). */
interface FastApiErrorBody {
  detail?: string | FastApiValidationError[]
}

export class HttpError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.name = 'HttpError'
    this.status = status
    this.body = body
  }
}

function isFastApiValidationError(value: unknown): value is FastApiValidationError {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as FastApiValidationError).loc) &&
    typeof (value as FastApiValidationError).msg === 'string'
  )
}

function formatValidationError(error: FastApiValidationError): string {
  const field = error.loc[error.loc.length - 1]
  const message = error.ctx?.reason ?? error.msg
  return typeof field === 'string' ? `${startCase(field)}: ${message}` : message
}

function extractHttpErrorMessage(error: HttpError): string | undefined {
  const body = error.body as FastApiErrorBody | undefined
  const detail = body?.detail
  if (typeof detail === 'string') {
    return detail
  }
  if (Array.isArray(detail) && detail.length > 0 && detail.every(isFastApiValidationError)) {
    return detail.map(formatValidationError).join(', ')
  }
  return undefined
}

/**
 * The one place every API-error call site should get a user-displayable
 * message from: unpacks the backend's FastAPI `detail` (a plain string for
 * 400-style errors, or an array of per-field `{loc, msg, ctx}` validation
 * errors for 422s). Falls back to a generic `Error`'s own `message`, then to
 * the caller-supplied `fallback`. Never read `error.body.detail` or
 * `error.message` directly at a call site — use this instead.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpError) {
    return extractHttpErrorMessage(error) ?? fallback
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

/** Every GET call must bust any intermediary cache — appended to the path, never left to a per-call-site query param. */
function withCacheDisabled(path: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}cache_disabled=true`
}

export async function httpClient<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const isGet = (init?.method ?? 'GET').toUpperCase() === 'GET'
  const url = isGet ? withCacheDisabled(path) : path
  // A FormData body (file uploads) must let the browser set its own
  // multipart Content-Type (with boundary) — forcing 'application/json'
  // here would send the body as an unparsable multipart payload.
  const isFormData = init?.body instanceof FormData
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...init,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `${token.token_type} ${token.access_token}` } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = undefined
    }
    throw new HttpError(res.status, body)
  }

  if (res.status === 204) {
    return undefined as T
  }

  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}
